import { useEffect, useState } from 'react';
import { useFormContext } from 'framework/form';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { AssignmentFormData, AssignmentType } from '../types';
import { AssignmentTargetType } from '../target/shared/assignmentTargetService';
import { PROJECT_MEMBERSHIP_ROLES_KEY } from 'domain/shared/core/constants';

const TEAM_LEAD_ROLE_ON_PROJECT = 'TL';
const DA_VINCI_PROJECT_TARGET_ID = 58;

export const isIndividualAssignment = (formData?: AssignmentFormData) => {
  return formData?.assignmentType === AssignmentType.INDIVIDUAL_ASSIGNMENT;
};

export const isGroupAssignment = (formData?: AssignmentFormData) => {
  return formData?.assignmentType === AssignmentType.GROUP_ASSIGNMENT;
};

const hasProjectContext = (formData?: AssignmentFormData) => formData?.target?.type === AssignmentTargetType.PROJECT;

const isTeamLead = (formData?: AssignmentFormData) =>
  formData?.roleOnProject === TEAM_LEAD_ROLE_ON_PROJECT ||
  formData?.membershipRole === PROJECT_MEMBERSHIP_ROLES_KEY.TEAM_LEAD;

const isDaVinciProjectTarget = (formData?: AssignmentFormData) =>
  formData?.target?.targetId === DA_VINCI_PROJECT_TARGET_ID;

const useAssignmentFormState = () => {
  const { data: formData, isDirty } = useFormContext();
  const [hasInitialEmployeeId, setHasInitialEmployeeId] = useState(false);
  const [hasInitialEmployeeGroup, setHasInitialEmployeeGroup] = useState(false);
  const { isFeatureEnabled } = useFeatureToggle();
  const isGroupAssignmentsEnabled = isFeatureEnabled(Features.GROUP_ASSIGNMENTS);
  const isSpofVisible = isIndividualAssignment(formData) && hasProjectContext(formData) && !formData?.bench;
  const isSecondaryLeadVisible =
    isIndividualAssignment(formData) && isTeamLead(formData) && isDaVinciProjectTarget(formData);
  const isStartedAssignment = !!formData?.from && new Date(formData.from) < new Date();

  useEffect(() => {
    if (!isDirty && formData?.employeeId) {
      setHasInitialEmployeeId(true);
    }
    if (!isDirty && formData?.employeeGroupId) {
      setHasInitialEmployeeGroup(true);
    }
  }, [formData, isDirty]);

  return {
    hasInitialEmployeeId,
    hasInitialEmployeeGroup,
    isGroupAssignmentsEnabled,
    isSpofVisible,
    isSecondaryLeadVisible,
    isStartedAssignment,
  };
};

export default useAssignmentFormState;
